import type { ReactNode } from "react";

import { FieldRow } from "@/admin/components/shared/detail-card";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { TierBadge } from "@/shared/components/ui/tier-badge";
import type { RegistrationConfiguredFieldItem } from "@/admin/lib/registration-configured-fields";

// A configured column that resolves a lifecycle status (e.g.
// ``entity:warranty:status``) renders as a status badge, not bare text, so it
// reads the same as the rest of the platform.
function isStatusColumn(key: string) {
  return key === "status" || key.endsWith(":status");
}

// The warranty tier column renders as its tier, for the same reason a status
// column renders as a status badge: it is a state, not a string. Only when the
// tenant has the tier capability on - otherwise it stays plain text.
function isTierColumn(key: string) {
  return key === "class_name" || key.endsWith(":class_name");
}

// Safely coerce an item to a renderable value. ``displayValue`` is undefined
// for object-typed values the detail-card formatter would normally handle
// (e.g. FILE objects); passing such a raw object to React as a child throws
// "Objects are not valid as a React child". Render those as a dash instead.
function fieldValue(item: RegistrationConfiguredFieldItem): ReactNode {
  if (item.displayValue !== undefined) return item.displayValue;
  if (item.value !== null && typeof item.value === "object") return "-";
  return item.value as ReactNode;
}

export function RegistrationConfiguredFieldRows({
  items,
  tierFinish,
}: {
  items: RegistrationConfiguredFieldItem[];
  /**
   * Finish token for the record's tier. Supplied by the page, which knows the
   * warranty; the configured-field items themselves carry only label + value.
   * Omit it and a tier column renders as plain text.
   */
  tierFinish?: string | null;
}) {
  return (
    <>
      {items.map((item) => (
        <FieldRow
          key={item.key}
          label={item.label}
          value={
            isStatusColumn(item.key) && !item.muted ? (
              <StatusBadge status={String(item.value)} />
            ) : isTierColumn(item.key) && tierFinish && !item.muted ? (
              <TierBadge name={String(item.value ?? "")} finish={tierFinish} />
            ) : (
              fieldValue(item)
            )
          }
          muted={item.muted}
        />
      ))}
    </>
  );
}
